/**
 * SeriesLinkButton — library-card control to link a story into a series
 * (box set). Sets series_key + series_part so the story appears on the
 * /books page, gets its own audiobook RSS feed, and shows binge
 * navigation (Part N of M, next episode) on its public page.
 */
import { useState } from "react";
import axios from "axios";
import { BookOpen, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeriesLinkButton({ video, onSaved }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [selKey, setSelKey] = useState("");
  const [newName, setNewName] = useState("");
  const [part, setPart] = useState("");
  const [busy, setBusy] = useState(false);

  const openModal = async () => {
    setOpen(true);
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/series`);
      const series = data.series || [];
      setList(series);
      if (video.series_key && series.some((s) => s.series_key === video.series_key)) {
        setSelKey(video.series_key);
        setPart(String(video.series_part ?? ""));
      } else {
        setSelKey(series.length > 0 ? series[0].series_key : "__new__");
        setPart("");
      }
      setNewName("");
    } catch (e) {
      toast.error("Could not load series list");
    } finally {
      setBusy(false);
    }
  };

  const save = async (clear = false) => {
    const name = clear ? "" : selKey === "__new__" ? newName.trim() : selKey;
    if (!clear && !name) {
      toast.error("Give the new series a name first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/videos/${video.id}/series`, {
        series_name: name || null,
        series_part: !clear && part.trim() !== "" ? parseInt(part, 10) : null,
      });
      toast.success(clear
        ? "Removed from series"
        : `Linked as Part ${data.series_part}`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update series");
    } finally {
      setBusy(false);
    }
  };

  const selected = list.find((s) => s.series_key === selKey);

  return (
    <>
      <button
        onClick={openModal}
        className={`text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border transition-colors flex items-center justify-center gap-1.5 ${video.series_key ? "border-[color:var(--accent)]/50 text-[color:var(--accent)]" : "border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"}`}
        data-testid={`library-series-${video.id}`}
        title={video.series_key
          ? `Part ${video.series_part} of "${video.series_key}" — click to change`
          : "Link this story into a series (box set) so viewers binge episodes in order"}
      >
        <BookOpen className="w-3.5 h-3.5" />
        {video.series_key ? `Part ${video.series_part}` : "Series"}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="series-modal-overlay"
        >
          <div
            className="panel w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="series-modal"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display text-lg leading-snug flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-[color:var(--accent)]" />
                  Link into a series
                </div>
                <div className="text-xs text-[color:var(--muted)] mt-1 line-clamp-1">
                  {video.title}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-[color:var(--muted)] hover:text-white transition-colors"
                data-testid="series-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="label-eyebrow mb-1 block">Series</label>
                <select
                  value={selKey}
                  onChange={(e) => { setSelKey(e.target.value); setPart(""); }}
                  className="w-full bg-[#141210] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                  data-testid="series-select"
                >
                  {list.map((s) => (
                    <option key={s.series_key} value={s.series_key}>
                      {s.title} ({s.count} {s.count === 1 ? "episode" : "episodes"})
                    </option>
                  ))}
                  <option value="__new__">＋ New series…</option>
                </select>
              </div>

              {selKey === "__new__" && (
                <div>
                  <label className="label-eyebrow mb-1 block">New series name</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. The Journey of England in the World Cup"
                    className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                    data-testid="series-new-name"
                    autoFocus
                  />
                </div>
              )}

              <div>
                <label className="label-eyebrow mb-1 block">
                  Part number
                  <span className="normal-case tracking-normal text-[color:var(--muted)]">
                    {" "}— leave blank for next{selected ? ` (${selected.next_part})` : selKey === "__new__" ? " (1)" : ""}
                  </span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={part}
                  onChange={(e) => setPart(e.target.value)}
                  placeholder={selected ? String(selected.next_part) : "1"}
                  className="w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[color:var(--accent)] outline-none"
                  data-testid="series-part-input"
                />
              </div>

              <div className="text-[11px] text-[color:var(--muted)] leading-relaxed">
                Linked stories appear as a book on the public <span className="text-white">/books</span> page,
                get a dedicated audiobook RSS feed, and show "Part N · Next episode" navigation on their story page.
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => save(false)}
                  disabled={busy}
                  className="btn-primary text-xs inline-flex items-center gap-2 px-4 py-2 disabled:opacity-40"
                  data-testid="series-save"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookOpen className="w-3.5 h-3.5" />}
                  {video.series_key ? "Update link" : "Link to series"}
                </button>
                {video.series_key && (
                  <button
                    onClick={() => save(true)}
                    disabled={busy}
                    className="text-xs px-4 py-2 rounded-lg border border-red-400/30 text-red-300 hover:border-red-400 transition-colors disabled:opacity-40"
                    data-testid="series-remove"
                  >
                    Remove from series
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
